import words from 'lodash/words'

import { publisherOptions, sortOptions } from './constants'
import type {
  Segment,
  VisibilityFilter,
  PublisherFilter,
  SortOption,
} from './types'

const ANY: VisibilityFilter = 'any'

export const FAKE_PIN_KEYS = {
  ALL_CLIENTS: 'all_clients',
  FOLLOWED_CLIENTS: 'followed_clients',
} as const

export type FakePinKey = typeof FAKE_PIN_KEYS[keyof typeof FAKE_PIN_KEYS]

// Pending-pin keys for fake pins (e.g. "all_clients", "followed_clients"),
// which have no segment to build a real segmentKey() from. Prefixed so they
// never collide with a real key: those are always `s:`/`p:` (segment-key.ts).
export const fakePinPendingKey = (key: FakePinKey): string => `fake:${key}`

export const compareStringsAsc = (a: string, b: string): number =>
  a.toLowerCase().localeCompare(b.toLowerCase())

export const compareByNameAsc = (a: Segment, b: Segment): number =>
  compareStringsAsc(a.name, b.name)

export const matchesSearch = (segment: Segment, term: string): boolean => {
  if (term.length === 0) return true
  const nameWords = words(segment.name.toLowerCase())
  // Each word of the search term must prefix-match some word in the name, so
  // a multi-word search like "end fin" still matches "End of Financial Year".
  return words(term).every((termWord) =>
    nameWords.some((nameWord) => nameWord.startsWith(termWord))
  )
}

export const matchesVisibility = (
  segment: Segment,
  filter: VisibilityFilter
): boolean => {
  if (filter === ANY) return true
  return segment.visibility === filter
}

export const matchesPublisher = (
  segment: Segment,
  filter: PublisherFilter
): boolean => {
  if (filter === publisherOptions.ALL) return true
  return segment.publisher === filter
}

export const sortSegments = (
  list: Segment[],
  option: SortOption
): Segment[] => {
  if (option === sortOptions.ALPHABETICALLY) {
    return [...list].sort(compareByNameAsc)
  }

  if (option === sortOptions.DATE_ADDED) {
    return [...list].sort((a, b) => {
      const dateA = a.created_at ? Date.parse(a.created_at) : 0
      const dateB = b.created_at ? Date.parse(b.created_at) : 0
      return dateA - dateB
    })
  }

  if (option === sortOptions.LAST_VISITED) {
    return [...list].sort((a, b) => {
      const dateA = a.last_visited_at ? Date.parse(a.last_visited_at) : 0
      const dateB = b.last_visited_at ? Date.parse(b.last_visited_at) : 0
      return dateB - dateA
    })
  }

  // default: recommended first (A-Z), then rest (A-Z)
  const { recommended, rest } = list.reduce<{
    recommended: Segment[]
    rest: Segment[]
  }>(
    (acc, s) => {
      if (s.recommended) acc.recommended.push(s)
      else acc.rest.push(s)
      return acc
    },
    { recommended: [], rest: [] }
  )
  return [...recommended.sort(compareByNameAsc), ...rest.sort(compareByNameAsc)]
}
